import Image from "../../components/Image";
import { lazy, Suspense, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Toolbar,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useLocation } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import { useLanguage } from "../../context/LanguageContext";
import { useUser } from "../../context/UserContext";
import BaseButton from "../../components/Button";
import ListLanguageButton from "./components/ListLanguageButton";
import styles from "./AppBar.styles";

const UserMenu = lazy(() => import("./components/UserMenu"));

const ApplicationBar = () => {
  const { translate } = useLanguage();
  const { user } = useUser();
  const pages = [
    { key: "platform", labelKey: "appbar platform", path: "/#platform" },
    { key: "features", labelKey: "appbar features", path: "/#features" },
    { key: "pricing", labelKey: "appbar pricing", path: "/#pricing" },
    { key: "faq", labelKey: "appbar faq", path: "/#faq" },
    { key: "downloads", labelKey: "appbar downloads", path: "/downloads" },
  ];
  const [anchorElNav, setAnchorElNav] = useState(null);
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const authRedirectState = { from: location.state?.from || currentPath };
  const isAuthPage = location.pathname === "/sign";
  const isSignupPage = isAuthPage && new URLSearchParams(location.search).get("mode") === "signup";

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(
      event.currentTarget.closest(".MuiToolbar-root") || event.currentTarget
    );
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar
      position="relative"
      sx={[styles.AppBar_container, isAuthPage && styles.AppBar_authContainer]}
      elevation={0}
    >
      <Container maxWidth={false} sx={styles.AppBar_inner}>
        <Toolbar disableGutters sx={[styles.AppBar_toolbar, isAuthPage && styles.AppBar_authToolbar]}>
          <Box sx={styles.AppBar_logo}>
            <Link to="/">
              <Image
                src={require("../../assets/logo.webp")}
                srcSet={`${require("../../assets/logo-small.webp")} 380w, ${require("../../assets/logo.webp")} 540w`}
                sizes="(max-width: 599px) 190px, (max-width: 899px) 250px, 400px"
                width={540}
                height={97}
                alt="Sharp View"
                sx={styles.AppBar_logoImage}
              />
            </Link>
          </Box>
          {!isAuthPage && (
            <Box sx={styles.AppBar_navButtons}>
              {pages.map((page) => (
                <Button
                  key={page.key}
                  component={Link}
                  to={page.path}
                  smooth
                  sx={styles.AppBar_navButton}
                  onClick={handleCloseNavMenu}
                >
                  {translate(page.labelKey)}
                </Button>
              ))}
            </Box>
          )}
          <Box sx={styles.AppBar_buttons}>
            {user ? (
              <>
                <ListLanguageButton />
                <Suspense fallback={<Box sx={{ width: 40, height: 40 }} />}><UserMenu /></Suspense>
              </>
            ) : (
              <>
                {isAuthPage ? (
                  <BaseButton
                    text={isSignupPage ? translate("appbar sign in") : translate("footer create account")}
                    styling="secondary"
                    to={isSignupPage ? "/sign" : "/sign?mode=signup"}
                    state={authRedirectState}
                    sx={[styles.AppBar_authButton, styles.AppBar_authPageAction]}
                    endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
                  />
                ) : (
                  <>
                    <BaseButton
                      text={translate("appbar sign in")}
                      styling="secondary"
                      to="/sign"
                      state={authRedirectState}
                      sx={styles.AppBar_authButton}
                      endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
                    />
                    <BaseButton
                      text={translate("appbar start free trial")}
                      styling="primary"
                      to="/sign?mode=signup"
                      state={authRedirectState}
                      sx={styles.AppBar_authButton}
                      endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
                    />
                  </>
                )}
                <ListLanguageButton />
              </>
            )}
          </Box>
          {!isAuthPage && <Box sx={styles.AppBar_menu}>
            <IconButton
              size="medium"
              aria-label={anchorElNav ? "Close navigation" : "Open navigation"}
              aria-controls={anchorElNav ? "menu-appbar" : undefined}
              aria-expanded={anchorElNav ? "true" : undefined}
              aria-haspopup="true"
              onClick={anchorElNav ? handleCloseNavMenu : handleOpenNavMenu}
              sx={styles.AppBar_menuIcon}
            >
              {anchorElNav ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              marginThreshold={0}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={styles.AppBar_menuContainer}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.key}
                  component={Link}
                  to={page.path}
                  smooth
                  onClick={handleCloseNavMenu}
                  sx={styles.AppBar_navMenuItem}
                >
                  {translate(page.labelKey)}
                </MenuItem>
              ))}
              {!user && (
                <Box sx={styles.AppBar_menuAuthActions}>
                  <BaseButton
                    text={translate("appbar start free trial")}
                    styling="primary"
                    to="/sign?mode=signup"
                    state={{ from: currentPath }}
                    handleOnClick={handleCloseNavMenu}
                    sx={styles.AppBar_menuAuthButton}
                    endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
                  />
                  <BaseButton
                    text={translate("appbar sign in")}
                    styling="secondary"
                    to="/sign"
                    state={{ from: currentPath }}
                    handleOnClick={handleCloseNavMenu}
                    sx={styles.AppBar_menuAuthButton}
                    endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
                  />
                </Box>
              )}
            </Menu>
          </Box>}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ApplicationBar;
